import { FileIcon, FileTextIcon, type Icon } from '@phosphor-icons/react';

import classNames from '@utils/classnames';

/** Dialog title · 15/600, quieter than `.heading-one` so the file rows lead. */
export const DOCUMENT_DIALOG_TITLE_CLASS = 'mb-4 text-base font-semibold text-fg';

interface DocumentFileRowProps {
  icon?: Icon;
  name: string;
  /** Optional muted line under the name (file name, state). */
  meta?: string;
  /** Grey out the row when it stands for a document that is not there yet. */
  muted?: boolean;
  actions?: React.ReactNode;
}

/** One document on a sunken row: icon, name and inline actions (open, delete …). */
export const DocumentFileRow: React.FC<DocumentFileRowProps> = ({ icon, name, meta, muted, actions }) => {
  const RowIcon = icon ?? FileTextIcon;
  return (
    <div className="flex min-h-[42px] items-center gap-2.5 rounded-lg border border-border bg-surface-sunken px-3 py-2">
      <RowIcon className="h-4 w-4 shrink-0 text-fg-muted" />
      <div className="min-w-0 flex-1">
        <div className={classNames('truncate text-xs', muted ? 'text-fg-muted' : 'text-fg')}>{name}</div>
        {meta && <div className="truncate text-2xs text-fg-muted">{meta}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </div>
  );
};

interface DocumentEmptyStateProps {
  text: string;
  /** Primary action for the empty state, e.g. a generate button. */
  children?: React.ReactNode;
}

/** Bordered empty state explaining why there is no file, with room for one action. */
export const DocumentEmptyState: React.FC<DocumentEmptyStateProps> = ({ text, children }) => (
  <div className="flex flex-col items-center justify-center gap-2.5 rounded-lg border border-border px-4 py-6 text-center">
    <FileIcon className="h-6 w-6 text-fg-muted" />
    <p className="text-xs text-fg-muted">{text}</p>
    {children}
  </div>
);

interface DocumentDialogFooterProps {
  /** Secondary action shown on the left (e.g. regenerate). */
  left?: React.ReactNode;
  /** Right-aligned actions, typically the close button. */
  children?: React.ReactNode;
}

export const DocumentDialogFooter: React.FC<DocumentDialogFooterProps> = ({ left, children }) => (
  <div className="mt-4 flex items-center gap-2">
    {left}
    <div className="flex-1" />
    {children}
  </div>
);
